import React, { useMemo } from "react";
import { FaChevronUp, FaFilter, FaSearch, FaTimes } from "react-icons/fa";
import { CMS_PRIMARY } from "../../TaskManagement/workspaceConstants";
import CommissionsSingleSelect from "./CommissionsMultiSelect";
import CommissionsStatusSelect from "./CommissionsStatusSelect";
import CommissionsDateRangeField from "./CommissionsDateRangeField";
import CommissionsActiveChips from "./CommissionsActiveChips";
import {
  APPLICATION_TYPES,
  COMMISSIONS_FILTER_ROW_CLASS,
  COMMISSIONS_FILTER_FIELD_CLASS,
  FILTER_CARD_CLASS,
  FILTER_CHEVRON_CLASS,
  FILTER_PANEL_INNER_CLASS,
  FILTER_SEARCH_CLASS,
  FILTER_LABEL_CLASS,
  FILTER_TRIGGER_CLASS,
  LOCATION_OPTIONS,
  getStatusGroups,
} from "./commissionsConstants";
import {
  buildActiveChips,
  countFilterSelections,
} from "./useCommissionsFilters";

const FILTER_BTN_RESET = {
  border: "none",
  outline: "none",
  WebkitAppearance: "none",
  appearance: "none",
};

function FilterFooter({ chips, onRemoveChip, onReset, filtersSelected }) {
  return (
    <div className="cms-commissions-filter-footer mt-3 flex w-full items-start justify-between gap-4 border-t border-[#E2E8F0] pt-3">
      <div className="flex min-w-0 flex-1 flex-wrap items-center justify-start gap-x-2 gap-y-1.5 text-left">
        {chips.length > 0 && (
          <>
            <span className="shrink-0 text-[13px] font-medium text-gray-500">
              Active Filters:
            </span>
            <CommissionsActiveChips chips={chips} onRemove={onRemoveChip} />
          </>
        )}
      </div>
      <div className="cms-commissions-filter-footer__actions ml-auto flex shrink-0 flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onReset}
          disabled={!filtersSelected}
          className={`inline-flex h-9 items-center rounded-lg border px-4 text-[13px] font-medium shadow-sm transition ${
            filtersSelected
              ? "cursor-pointer border-[#E5E7EB] bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
              : "cursor-not-allowed border-[#E5E7EB] bg-white text-gray-400 opacity-50"
          }`}
          style={FILTER_BTN_RESET}
        >
          Reset
        </button>
      </div>
    </div>
  );
}

function CommissionsSearchFilters({
  type,
  onTypeChange,
  searchTerm,
  onSearchChange,
  onSearchClear,
  filtersOpen,
  onToggleFilters,
  activeFilterCount,
  draft,
  applied = draft,
  onDraftChange,
  providersList,
  coursesList,
  intakes,
  processingAgentsList = [],
  referringAgentsList = [],
  processingBranchesList = [],
  referringBranchesList = [],
  onReset,
  onRemoveChip,
  suppressProviderFilter = false,
}) {
  const statusGroups = useMemo(() => getStatusGroups(draft.type), [draft.type]);
  const statusLabel =
    draft.type === "education" ? "Status (Education)" : "Status (Migration)";

  const chips = useMemo(
    () =>
      buildActiveChips(
        draft,
        providersList,
        coursesList,
        intakes,
        processingAgentsList,
        referringAgentsList,
        processingBranchesList,
        referringBranchesList,
      ),
    [draft, providersList, coursesList, intakes, processingAgentsList, referringAgentsList, processingBranchesList, referringBranchesList],
  );

  const filtersSelected = useMemo(
    () =>
      countFilterSelections(draft) > 0 || countFilterSelections(applied) > 0,
    [draft, applied],
  );

  const handleFilterSubmit = (e) => {
    e.preventDefault();
  };

  const isEducation = draft.type === "education";

  return (
    <div className={FILTER_CARD_CLASS}>
      {/* Toolbar: type tabs left | search + filters right */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex shrink-0 overflow-hidden rounded-md border border-[#E5E7EB] bg-white shadow-sm">
          {APPLICATION_TYPES.map((t, index) => {
            const active = type === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => onTypeChange(t.id)}
                className={`h-9 px-3.5 text-[13px] font-semibold transition ${
                  index > 0 ? "border-l border-[#E5E7EB]" : ""
                } ${
                  active
                    ? "bg-[#007BFF] text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
                style={{ outline: "none", boxShadow: "none", WebkitAppearance: "none", appearance: "none" }}
              >
                {t.title}
              </button>
            );
          })}
          {/* Migration tab temporarily hidden
          <button
            type="button"
            onClick={() => onTypeChange("migration")}
            className={`h-9 border-l border-[#E5E7EB] px-3.5 text-[13px] font-semibold transition ${
              type === "migration"
                ? "bg-[#007BFF] text-white"
                : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
            style={{ outline: "none", boxShadow: "none", WebkitAppearance: "none", appearance: "none" }}
          >
            Migration
          </button>
          */}
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-2">
          <div className="relative w-full min-w-0 sm:w-[min(360px,70vw)] sm:min-w-[240px]">
            <FaSearch
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
              size={13}
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Name, client/user/app ID, DOB (DD/MM/YYYY), provider/course..."
              className={FILTER_SEARCH_CLASS}
              autoComplete="off"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={onSearchClear}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
                style={{ border: "none", outline: "none", background: "transparent", padding: 0 }}
              >
                <FaTimes size={11} />
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={onToggleFilters}
            aria-expanded={filtersOpen}
            className={`inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border px-2.5 text-[13px] font-medium shadow-sm transition ${
              filtersOpen
                ? "border-[#007BFF]/40 bg-[#007BFF]/5 text-[#007BFF]"
                : "border-[#E5E7EB] bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
            }`}
          >
            <FaFilter size={12} className="text-gray-500" />
            Filters
            {activeFilterCount > 0 && (
              <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#007BFF] px-1 text-[11px] font-bold text-white">
                {activeFilterCount}
              </span>
            )}
            <FaChevronUp
              size={10}
              className={`${FILTER_CHEVRON_CLASS} ${filtersOpen ? "" : "rotate-180"}`}
            />
          </button>
        </div>
      </div>

      {filtersOpen && (
        <form
          className={`mt-3 ${FILTER_PANEL_INNER_CLASS}`}
          onSubmit={handleFilterSubmit}
        >
          {isEducation ? (
            <>
              <div className={`${COMMISSIONS_FILTER_ROW_CLASS} mb-2.5`}>
                {suppressProviderFilter ? null : (
                  <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                    <CommissionsSingleSelect
                      label="Provider"
                      value={draft.providerId}
                      onChange={(providerId) => {
                        onDraftChange({
                          ...draft,
                          providerId,
                          courseId: null,
                        });
                      }}
                      options={providersList}
                      placeholder="All Providers"
                      searchPlaceholder="Search providers..."
                    />
                  </div>
                )}
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <CommissionsSingleSelect
                    label="Course / Package"
                    value={draft.courseId}
                    onChange={(courseId) => onDraftChange({ ...draft, courseId })}
                    options={coursesList}
                    placeholder="All Courses"
                    searchPlaceholder="Search courses..."
                    disabled={!draft.providerId}
                  />
                </div>
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <CommissionsSingleSelect
                    label="Intake"
                    value={draft.intakeId}
                    onChange={(intakeId) => onDraftChange({ ...draft, intakeId })}
                    options={intakes}
                    placeholder="All Intakes"
                    searchPlaceholder="Search intakes..."
                  />
                </div>
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <CommissionsSingleSelect
                    label="Location"
                    value={draft.location}
                    onChange={(location) => onDraftChange({ ...draft, location })}
                    options={LOCATION_OPTIONS}
                    placeholder="All Locations"
                    searchPlaceholder="Search locations..."
                  />
                </div>
              </div>

              <div className={`${COMMISSIONS_FILTER_ROW_CLASS} mb-2.5`}>
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <CommissionsSingleSelect
                    label="Processing Agent"
                    value={draft.processingAgentId}
                    onChange={(processingAgentId) =>
                      onDraftChange({ ...draft, processingAgentId })
                    }
                    options={processingAgentsList}
                    placeholder="All Processing Agents"
                    searchPlaceholder="Search processing agents..."
                  />
                </div>
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <CommissionsSingleSelect
                    label="Referring Agent"
                    value={draft.referringAgentId}
                    onChange={(referringAgentId) =>
                      onDraftChange({ ...draft, referringAgentId })
                    }
                    options={referringAgentsList}
                    placeholder="All Referring Agents"
                    searchPlaceholder="Search referring agents..."
                  />
                </div>
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <CommissionsSingleSelect
                    label="Processing Branch"
                    value={draft.processingBranchId}
                    onChange={(processingBranchId) =>
                      onDraftChange({ ...draft, processingBranchId })
                    }
                    options={processingBranchesList}
                    placeholder="All Processing Branches"
                    searchPlaceholder="Search processing branches..."
                  />
                </div>
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <CommissionsSingleSelect
                    label="Referring Branch"
                    value={draft.referringBranchId}
                    onChange={(referringBranchId) =>
                      onDraftChange({ ...draft, referringBranchId })
                    }
                    options={referringBranchesList}
                    placeholder="All Referring Branches"
                    searchPlaceholder="Search referring branches..."
                  />
                </div>
              </div>

              <div className={`${COMMISSIONS_FILTER_ROW_CLASS} mt-2.5`}>
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <label className={FILTER_LABEL_CLASS} htmlFor="commissions-client-user-id">
                    Client / User ID
                  </label>
                  <input
                    id="commissions-client-user-id"
                    type="text"
                    inputMode="numeric"
                    value={draft.clientUserId || ""}
                    onChange={(e) =>
                      onDraftChange({ ...draft, clientUserId: e.target.value })
                    }
                    placeholder="e.g. 256"
                    className={FILTER_TRIGGER_CLASS}
                    autoComplete="off"
                  />
                </div>
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <label className={FILTER_LABEL_CLASS} htmlFor="commissions-client-dob">
                    Date of birth
                  </label>
                  <input
                    id="commissions-client-dob"
                    type="date"
                    value={draft.clientDob || ""}
                    onChange={(e) =>
                      onDraftChange({ ...draft, clientDob: e.target.value })
                    }
                    className={FILTER_TRIGGER_CLASS}
                  />
                </div>
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <CommissionsDateRangeField
                    value={draft.dateRange}
                    onChange={(dateRange) => onDraftChange({ ...draft, dateRange })}
                  />
                </div>
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <CommissionsStatusSelect
                    label={statusLabel}
                    value={draft.statuses}
                    onChange={(statuses) => onDraftChange({ ...draft, statuses })}
                    statusGroups={statusGroups}
                    placeholder="All Statuses"
                    multiple
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={`${COMMISSIONS_FILTER_ROW_CLASS} mb-2.5`}>
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <CommissionsSingleSelect
                    label="Provider"
                    value={draft.providerId}
                    onChange={(providerId) => {
                      onDraftChange({
                        ...draft,
                        providerId,
                        courseId: null,
                      });
                    }}
                    options={providersList}
                    placeholder="All Providers"
                    searchPlaceholder="Search providers..."
                  />
                </div>
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <CommissionsSingleSelect
                    label="Course / Package"
                    value={draft.courseId}
                    onChange={(courseId) => onDraftChange({ ...draft, courseId })}
                    options={coursesList}
                    placeholder="All Courses"
                    searchPlaceholder="Search courses..."
                    disabled={!draft.providerId}
                  />
                </div>
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <CommissionsSingleSelect
                    label="Location"
                    value={draft.location}
                    onChange={(location) => onDraftChange({ ...draft, location })}
                    options={LOCATION_OPTIONS}
                    placeholder="All Locations"
                    searchPlaceholder="Search locations..."
                  />
                </div>
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <CommissionsSingleSelect
                    label="Processing Agent"
                    value={draft.processingAgentId}
                    onChange={(processingAgentId) =>
                      onDraftChange({ ...draft, processingAgentId })
                    }
                    options={processingAgentsList}
                    placeholder="All Processing Agents"
                    searchPlaceholder="Search processing agents..."
                  />
                </div>
              </div>

              <div className={`${COMMISSIONS_FILTER_ROW_CLASS} mb-2.5`}>
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <CommissionsSingleSelect
                    label="Referring Agent"
                    value={draft.referringAgentId}
                    onChange={(referringAgentId) =>
                      onDraftChange({ ...draft, referringAgentId })
                    }
                    options={referringAgentsList}
                    placeholder="All Referring Agents"
                    searchPlaceholder="Search referring agents..."
                  />
                </div>
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <CommissionsSingleSelect
                    label="Processing Branch"
                    value={draft.processingBranchId}
                    onChange={(processingBranchId) =>
                      onDraftChange({ ...draft, processingBranchId })
                    }
                    options={processingBranchesList}
                    placeholder="All Processing Branches"
                    searchPlaceholder="Search processing branches..."
                  />
                </div>
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <CommissionsSingleSelect
                    label="Referring Branch"
                    value={draft.referringBranchId}
                    onChange={(referringBranchId) =>
                      onDraftChange({ ...draft, referringBranchId })
                    }
                    options={referringBranchesList}
                    placeholder="All Referring Branches"
                    searchPlaceholder="Search referring branches..."
                  />
                </div>
              </div>

              <div className={`${COMMISSIONS_FILTER_ROW_CLASS} mt-2.5`}>
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <label className={FILTER_LABEL_CLASS} htmlFor="commissions-client-user-id-mig">
                    Client / User ID
                  </label>
                  <input
                    id="commissions-client-user-id-mig"
                    type="text"
                    inputMode="numeric"
                    value={draft.clientUserId || ""}
                    onChange={(e) =>
                      onDraftChange({ ...draft, clientUserId: e.target.value })
                    }
                    placeholder="e.g. 256"
                    className={FILTER_TRIGGER_CLASS}
                    autoComplete="off"
                  />
                </div>
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <label className={FILTER_LABEL_CLASS} htmlFor="commissions-client-dob-mig">
                    Date of birth
                  </label>
                  <input
                    id="commissions-client-dob-mig"
                    type="date"
                    value={draft.clientDob || ""}
                    onChange={(e) =>
                      onDraftChange({ ...draft, clientDob: e.target.value })
                    }
                    className={FILTER_TRIGGER_CLASS}
                  />
                </div>
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <CommissionsDateRangeField
                    value={draft.dateRange}
                    onChange={(dateRange) => onDraftChange({ ...draft, dateRange })}
                  />
                </div>
                <div className={COMMISSIONS_FILTER_FIELD_CLASS}>
                  <CommissionsStatusSelect
                    label={statusLabel}
                    value={draft.statuses}
                    onChange={(statuses) => onDraftChange({ ...draft, statuses })}
                    statusGroups={statusGroups}
                    placeholder="All Statuses"
                    multiple={false}
                  />
                </div>
              </div>
            </>
          )}

          <FilterFooter
            chips={chips}
            onRemoveChip={onRemoveChip}
            onReset={onReset}
            filtersSelected={filtersSelected}
          />
        </form>
      )}

      {!filtersOpen && chips.length > 0 && (
        <form
          className={`mt-3 ${FILTER_PANEL_INNER_CLASS}`}
          onSubmit={handleFilterSubmit}
        >
          <FilterFooter
            chips={chips}
            onRemoveChip={onRemoveChip}
            onReset={onReset}
            filtersSelected={filtersSelected}
          />
        </form>
      )}
    </div>
  );
}

export default CommissionsSearchFilters;